import React, { useState } from 'react'
import moment from 'moment'
import { ClickAwayListener, Drawer, Menu, MenuItem, Popper, Typography, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AccessTimeIcon from '@material-ui/icons/WatchLater'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import PersonIcon from '@material-ui/icons/Person'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import EmailIcon from '@material-ui/icons/MailOutline'
import PhoneIcon from '@material-ui/icons/Phone'
import BusinessIcon from '@material-ui/icons/Business'
import StarIcon from '@material-ui/icons/Star'
import { DescriptionIcon, MoneyCheckDollar } from '@paintscout/ui/icons'
import HtmlContent from '@paintscout/ui/components/HtmlContent'
import { useSnackbar } from 'notistack'
import classNames from 'classnames'

import type { CalendarEvent, Crew } from 'paintscout'
import type { User } from '@paintscout/api'

import { Button, IconButton, Paper, useIsMobile } from '@paintscout/ui'
import {
  formatCurrency,
  formatName,
  formatPhoneNumber,
  getCrews,
  getQuoteTypeOption,
  getTimezone,
  translateEventType
} from '@paintscout/util/builder'
import { escapeHref, removeAuth0Prefix } from '@paintscout/util'

import EstimatorAvatar from '../EstimatorAvatar'
import ProgressBar from '../ProgressBar'
import { useClientOptions } from '../../providers/ClientOptionsProvider'

const GOOGLE_CALENDAR_EVENT_TYPE = '_google-calendar_'
const DATE_FORMAT_FULL = 'MMM D, YYYY'

export interface EventPreviewPopperProps {
  event: CalendarEvent
  anchorEl: HTMLElement | null
  setAnchorEl: (el: HTMLElement | null) => void
  users: User[]
  onClose: () => void
  onEditEvent: (event: CalendarEvent) => void
  onCancelEvent: (event: CalendarEvent) => void
  onProgressChange?: (event: CalendarEvent, progress: number) => void
  onCrewChange?: (event: CalendarEvent, crewId: string) => void
  /**
   * Force the production layout regardless of `event.production`. Used by the
   * production calendar's wrapper, which is by definition production-only.
   */
  forceProduction?: boolean
  /**
   * Optional client-side navigation handler. When provided, used for deal /
   * contact / company links instead of a full page reload via window.location.
   */
  onNavigate?: (path: string) => void
  minWidth?: number
  /** When true, hides edit/delete footer and disables progress and crew editing. */
  readOnly?: boolean
}

const useStyles = makeStyles((theme) => ({
  drawer: {},
  drawerPaper: {
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    maxHeight: '85vh',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    '@supports (max-height: 85dvh)': {
      maxHeight: '85dvh'
    }
  },
  paper: {
    position: 'relative',
    boxShadow: `${theme.shadows[2]} !important`,
    borderRadius: theme.borderRadius?.md ?? 8,
    overflow: 'hidden'
  },
  scroll: {
    [theme.breakpoints.up('md')]: {
      maxHeight: '70vh',
      overflowY: 'auto'
    },
    [theme.breakpoints.down('sm')]: {
      overflowY: 'auto'
    }
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(2, 2, 1.5, 2),
    [theme.breakpoints.down('sm')]: {
      position: 'sticky',
      top: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: theme.zIndex.appBar + 1
    }
  },
  headerText: {
    flex: 1,
    minWidth: 0
  },
  headerTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  headerSubtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
    fontSize: '0.875rem'
  },
  closeButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1)
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    minHeight: 56
  },
  rowSingleLine: {
    alignItems: 'center',
    '& $rowIcon': {
      marginTop: theme.spacing(0.5)
    }
  },
  rowIcon: {
    color: theme.palette.text.secondary,
    fontSize: '1.25rem',
    marginTop: 2,
    flexShrink: 0
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  rowLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500
  },
  rowValuePrimary: {
    color: theme.palette.text.primary,
    fontSize: '1rem',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  rowValueSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  },
  link: {
    cursor: 'pointer',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: theme.paintscout?.palette?.brand?.Blue?.Blue55 ?? theme.palette.primary.main,
        '& $rowValuePrimary, & $rowValueSecondary, & $crewLabel': {
          color: theme.paintscout?.palette?.brand?.Blue?.Blue55 ?? theme.palette.primary.main
        }
      }
    }
  },
  pill: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  contactGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  dealRowInline: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0
  },
  subRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    textDecoration: 'none',
    color: 'inherit'
  },
  subRowIcon: {
    color: theme.palette.text.secondary,
    fontSize: '1.25rem',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center'
  },
  preferredStar: {
    color: '#f5a622',
    fontSize: '1rem',
    flexShrink: 0
  },
  inlineValue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  estimatorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  estimatorName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  crewBlobWrap: {
    display: 'inline-flex',
    alignItems: 'stretch',
    flexShrink: 0
  },
  crewBlob: {
    display: 'inline-block',
    width: 5,
    minHeight: 22,
    alignSelf: 'stretch',
    borderRadius: 4,
    flexShrink: 0
  },
  crewMenuItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1)
  },
  crewMenuItemText: {
    display: 'flex',
    flexDirection: 'column'
  },
  crewMenuItemSubtext: {
    fontSize: '0.75rem',
    color: '#a2afc3',
    lineHeight: 1.4
  },
  crewLabel: {
    fontSize: '0.95rem',
    color: theme.palette.text.primary
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      position: 'sticky',
      bottom: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: theme.zIndex.appBar + 1
    }
  },
  cancelButton: {
    minWidth: 0,
    justifyContent: 'flex-start',
    '& > span': {
      color: theme.palette.error.main
    }
  }
}))

interface RowProps {
  icon: React.ReactNode
  onClick?: () => void
  className?: string
  children: React.ReactNode
}

function Row({ icon, onClick, className, children }: RowProps) {
  const classes = useStyles()
  return (
    <div
      className={classNames(classes.row, { [classes.link]: Boolean(onClick) }, className)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <span className={classes.rowIcon}>{icon}</span>
      <div className={classes.rowBody}>{children}</div>
    </div>
  )
}

interface ContactSectionProps {
  event: CalendarEvent
  contactName: string
  mobile: boolean
  isProduction: boolean
  navigateTo: (path: string) => void
}

function ContactSection({ event, contactName, mobile, isProduction, navigateTo }: ContactSectionProps) {
  const classes = useStyles()
  const showAddressInGroup = mobile && isProduction && !!event?.address
  if (!event?.contact && !showAddressInGroup) return null

  const contactId = event?.contact?.id
  const onContactPage = onPagePath(contactId)
  const email = event?.contact?.email
  const phone = event?.contact?.phoneNumber
  const phoneFormatted = phone ? formatPhoneNumber({ phoneNumber: phone }) : ''

  const showName = !!contactName
  const showEmail = !!email && !isProduction
  const showPhone = !!phone

  const preferred = event?.contact?.preferredContactMethod
  const isEmailPreferred = preferred === 'email' && showEmail
  const isPhonePreferred = (preferred === 'phone' || preferred === 'sms') && showPhone

  if (mobile) {
    if (!showName && !showEmail && !showPhone && !showAddressInGroup) return null
    return (
      <div className={classes.contactGroup}>
        {showName && (
          <div
            className={classes.subRow}
            onClick={contactId && !onContactPage ? () => navigateTo(`/people/contact/${contactId}`) : undefined}
          >
            <span className={classes.subRowIcon}>
              <PersonIcon fontSize="small" />
            </span>
            <span className={classes.pill}>{contactName}</span>
          </div>
        )}
        {!!event?.contact?.companyName && (
          <div
            className={classes.subRow}
            onClick={
              event.contact.companyId && !onPagePath(event.contact.companyId)
                ? () => navigateTo(`/people/companies/${event.contact!.companyId}`)
                : undefined
            }
          >
            <span className={classes.subRowIcon}>
              <BusinessIcon fontSize="small" />
            </span>
            <span className={classes.pill}>{event.contact.companyName}</span>
          </div>
        )}
        {showAddressInGroup && (
          <div
            className={classes.subRow}
            onClick={() =>
              window.open(
                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address ?? '')}`,
                '_blank'
              )
            }
          >
            <span className={classes.subRowIcon}>
              <LocationOnIcon fontSize="small" />
            </span>
            <span className={classes.pill}>{event.address}</span>
          </div>
        )}
        {showEmail && (
          <a className={classes.subRow} href={escapeHref(`mailto:${email}`)} onClick={(e) => e.stopPropagation()}>
            <span className={classes.subRowIcon}>
              <EmailIcon fontSize="small" />
            </span>
            <span className={classes.pill}>{email}</span>
            {isEmailPreferred && <StarIcon className={classes.preferredStar} />}
          </a>
        )}
        {showPhone && (
          <a className={classes.subRow} href={escapeHref(`tel:${phone}`)} onClick={(e) => e.stopPropagation()}>
            <span className={classes.subRowIcon}>
              <PhoneIcon fontSize="small" />
            </span>
            <span className={classes.pill}>{phoneFormatted}</span>
            {isPhonePreferred && <StarIcon className={classes.preferredStar} />}
          </a>
        )}
      </div>
    )
  }

  if (isProduction) {
    if (!showName && !showPhone) return null
    return (
      <Row
        icon={<PersonIcon fontSize="small" />}
        onClick={contactId && !onContactPage ? () => navigateTo(`/people/contact/${contactId}`) : undefined}
      >
        {showName && <Typography className={classes.rowValuePrimary}>{contactName}</Typography>}
        {showPhone && (
          <span className={classes.inlineValue}>
            <Typography
              className={classNames(classes.rowValueSecondary, classes.link)}
              onClick={(e) => {
                e.stopPropagation()
                window.location.href = escapeHref(`tel:${phone}`)
              }}
            >
              {phoneFormatted}
            </Typography>
            {isPhonePreferred && <StarIcon className={classes.preferredStar} />}
          </span>
        )}
      </Row>
    )
  }

  return (
    <Row icon={<PersonIcon fontSize="small" />}>
      <Typography className={classes.rowValuePrimary}>Contact</Typography>
      {showName && (
        <Typography
          className={classNames(classes.rowValueSecondary, {
            [classes.link]: Boolean(contactId) && !onContactPage
          })}
          onClick={
            contactId && !onContactPage
              ? (e) => {
                  e.stopPropagation()
                  navigateTo(`/people/contact/${contactId}`)
                }
              : undefined
          }
        >
          {contactName}
        </Typography>
      )}
      {showEmail && (
        <span className={classes.inlineValue}>
          <Typography
            className={classNames(classes.rowValueSecondary, classes.link)}
            onClick={(e) => {
              e.stopPropagation()
              window.location.href = escapeHref(`mailto:${email}`)
            }}
          >
            {email}
          </Typography>
          {isEmailPreferred && <StarIcon className={classes.preferredStar} />}
        </span>
      )}
      {showPhone && (
        <span className={classes.inlineValue}>
          <Typography
            className={classNames(classes.rowValueSecondary, classes.link)}
            onClick={(e) => {
              e.stopPropagation()
              window.location.href = escapeHref(`tel:${phone}`)
            }}
          >
            {phoneFormatted}
          </Typography>
          {isPhonePreferred && <StarIcon className={classes.preferredStar} />}
        </span>
      )}
    </Row>
  )
}

interface CrewRowProps {
  event: CalendarEvent
  matchedCrew: Crew | null | undefined
  allCrews: Crew[]
  onCrewChange?: (event: CalendarEvent, crewId: string) => void
}

function CrewRow({ event, matchedCrew, allCrews, onCrewChange }: CrewRowProps) {
  const classes = useStyles()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const interactive = !!onCrewChange
  if (!matchedCrew && !interactive) return null

  const crewColor = matchedCrew?.color ?? '#cbd5e1'
  const crewName = matchedCrew ? `${matchedCrew.name}${matchedCrew.trashed ? ' (Deleted)' : ''}` : 'Select crew'

  const handleOpen = interactive ? (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget) : undefined
  const handleClose = () => setAnchor(null)
  const handleSelect = (crewId: string) => {
    handleClose()
    onCrewChange?.(event, crewId)
  }

  const activeCrews = interactive ? allCrews.filter((crew) => !crew.trashed) : []
  const showTrashedCurrent = interactive && matchedCrew?.trashed

  return (
    <>
      <div
        className={classNames(classes.row, { [classes.link]: interactive })}
        onClick={handleOpen}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
      >
        <span className={classes.crewBlobWrap}>
          <span className={classes.crewBlob} style={{ backgroundColor: crewColor }} />
        </span>
        <div className={classes.rowBody}>
          <Typography className={classes.crewLabel}>{crewName}</Typography>
        </div>
      </div>
      {interactive && (
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={handleClose}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ style: { maxHeight: 320 } }}
        >
          {showTrashedCurrent && (
            <MenuItem disabled className={classes.crewMenuItem}>
              <span className={classes.crewBlob} style={{ backgroundColor: matchedCrew!.color }} />
              <div className={classes.crewMenuItemText}>{matchedCrew!.name} (Deleted)</div>
            </MenuItem>
          )}
          {activeCrews.map((crew) => {
            const leadName = crew.projectManager ? formatName(crew.projectManager) : null
            return (
              <MenuItem
                key={crew._id}
                className={classes.crewMenuItem}
                selected={crew._id === matchedCrew?._id}
                onClick={() => handleSelect(crew._id)}
              >
                <span className={classes.crewBlob} style={{ backgroundColor: crew.color }} />
                <div className={classes.crewMenuItemText}>
                  {crew.name}
                  {leadName && <span className={classes.crewMenuItemSubtext}>Lead: {leadName}</span>}
                </div>
              </MenuItem>
            )
          })}
          {activeCrews.length === 0 && !showTrashedCurrent && <MenuItem disabled>No crews available</MenuItem>}
        </Menu>
      )}
    </>
  )
}

function onPagePath(id?: string) {
  if (!id || typeof window === 'undefined') return false
  return window.location.pathname.includes(id)
}

export default function EventPreviewPopper(props: EventPreviewPopperProps) {
  const {
    event,
    anchorEl,
    setAnchorEl,
    onClose,
    onEditEvent,
    onCancelEvent,
    onProgressChange,
    onCrewChange,
    forceProduction,
    onNavigate,
    users,
    minWidth,
    readOnly
  } = props
  const navigateTo = (path: string) => {
    if (onNavigate) onNavigate(path)
    else window.location.href = escapeHref(path)
  }
  const classes = useStyles()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const mobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()

  const isGoogleCalendar = event?.eventType === GOOGLE_CALENDAR_EVENT_TYPE
  const isProduction = forceProduction ?? !!event?.production
  const eventTypeLabel = event?.eventType ? translateEventType({ eventType: event.eventType, options }) : ''
  const projectTypeLabel = event?.projectType
    ? getQuoteTypeOption({ options, quoteType: event.projectType })?.label ?? ''
    : ''
  const contactName = event?.contact ? formatName(event.contact) : ''

  const allCrews = getCrews({ options, includeTrashed: true })
  const matchedCrew = event?.crew ? allCrews.find((crew) => crew._id === event.crew) : null

  const estimatorUser = event?.estimator
    ? users?.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(event.estimator?.id))
    : null

  const headerTitle = (() => {
    if (isGoogleCalendar) {
      const titleStr = typeof event?.title === 'string' ? event.title : ''
      return titleStr || 'Google Calendar Event'
    }
    if (isProduction) return event?.deal?.name || projectTypeLabel || eventTypeLabel || 'Event'
    if (eventTypeLabel && contactName) return `${eventTypeLabel} - ${contactName}`
    return contactName || eventTypeLabel || 'Event'
  })()

  const headerSubtitle = (() => {
    if (isGoogleCalendar) {
      return `Google (${event?.transparency === 'transparent' ? 'Free' : 'Busy'})`
    }
    if (isProduction) {
      const typeLabel = projectTypeLabel || eventTypeLabel
      if (mobile) return typeLabel || ''
      const parts = [event?.address, typeLabel].filter(Boolean)
      return parts.join(' • ')
    }
    if (mobile) return ''
    return event?.address || ''
  })()

  const handleCopy = (value: string, label: string) => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      enqueueSnackbar('Copy not available in this browser', { variant: 'warning' })
      return
    }
    navigator.clipboard
      .writeText(value)
      .then(() => enqueueSnackbar(`${label} Copied`, { variant: 'success' }))
      .catch(() => enqueueSnackbar(`Could not copy ${label}`, { variant: 'error' }))
  }

  const handleClose = () => {
    if (anchorEl) setAnchorEl(null)
    onClose()
  }

  const handleEdit = () => {
    if (anchorEl) setAnchorEl(null)
    onEditEvent(event)
  }

  const handleCancelOrDelete = () => {
    if (anchorEl) setAnchorEl(null)
    onCancelEvent(event)
  }

  const cancelLabel = isProduction ? 'Delete' : 'Cancel'
  const isPastEvent = !isProduction && !!event?.end && moment(event.end).isBefore(moment())
  const hideFooter = event?.canceled || isGoogleCalendar || readOnly

  const anchorWidth = anchorEl ? anchorEl.getBoundingClientRect().width : undefined
  const MAX_DESKTOP_WIDTH = 420
  const width = mobile ? '100%' : Math.min(Math.max(anchorWidth ?? 0, minWidth ?? 320), MAX_DESKTOP_WIDTH)

  const handleClickAway = (e: React.MouseEvent<Document, MouseEvent>) => {
    // Callers may pass a virtual anchor (just `getBoundingClientRect`) when the
    // popper is positioned at a screen coordinate, so guard `.contains` — a
    // direct call would throw on the virtual object and silently abort the
    // close.
    if (anchorEl?.contains?.(e.target as Node)) return
    // Don't close on clicks inside child MUI portals (e.g. the crew Menu) — they
    // live outside this popper's DOM via createPortal, so a mousedown on a menu
    // item otherwise unmounts the popper before the menu's onClick can fire.
    // JSS generates suffixed class names here (e.g. `MuiPopover-root-305`), so
    // `.MuiPopover-root` misses them — substring match catches both variants.
    const target = e.target as HTMLElement | null
    if (
      target?.closest?.(
        '[class*="MuiPopover-root"], [class*="MuiMenu-root"], [class*="MuiDialog-root"], [class*="MuiModal-root"], [class*="MuiDrawer-root"]'
      )
    )
      return
    onClose()
  }

  // ---- Row builders ----

  const renderTimeRow = () => {
    if (!event?.start || !event?.end) return null

    if (isProduction) {
      const startStr = moment(event.start).tz(timezone).format(DATE_FORMAT_FULL)
      const endStr = moment(event.end).tz(timezone).format(DATE_FORMAT_FULL)
      const hours = event.deal?.hours
      const copyValue =
        typeof hours === 'number' ? `${startStr} → ${endStr} (Estimated Hours: ${hours})` : `${startStr} → ${endStr}`
      return (
        <Row icon={<AccessTimeIcon fontSize="small" />} onClick={() => handleCopy(copyValue, 'Date')}>
          <Typography className={classes.rowValuePrimary}>
            {startStr} → {endStr}
          </Typography>
          {typeof hours === 'number' && (
            <Typography className={classes.rowValueSecondary}>Estimated Hours: {hours}</Typography>
          )}
        </Row>
      )
    }

    const startM = moment(event.start).tz(timezone)
    const endM = moment(event.end).tz(timezone)
    const isAllDay = event.allDay || !startM.isSame(endM, 'day')

    if (isAllDay) {
      // RBC stores all-day end as midnight of the day AFTER the last day;
      // subtract an hour so the displayed range ends on the actual last day.
      const lastDay = endM.clone().subtract(1, 'hour')
      const isSingleDay = startM.isSame(lastDay, 'day')

      let rangeStr: string
      if (isSingleDay) {
        rangeStr = startM.format(DATE_FORMAT_FULL)
      } else if (startM.year() === lastDay.year()) {
        rangeStr = `${startM.format('MMM D')} – ${lastDay.format('MMM D, YYYY')}`
      } else {
        rangeStr = `${startM.format(DATE_FORMAT_FULL)} – ${lastDay.format(DATE_FORMAT_FULL)}`
      }

      return (
        <Row
          icon={<AccessTimeIcon fontSize="small" />}
          className={classes.rowSingleLine}
          onClick={() => handleCopy(rangeStr, 'Date')}
        >
          <Typography className={classes.rowValuePrimary}>{rangeStr}</Typography>
        </Row>
      )
    }

    const startStr = startM.format('h:mma')
    const endStr = endM.format('h:mma')
    // Zero-duration imports (Zapier, GCal) save start === end. Calendar.tsx pads
    // these to a 30-min slot for grid visibility; collapse the redundant
    // "1:00pm - 1:00pm" here so the popover reflects the actual saved data.
    const time = startM.isSame(endM) ? startStr : `${startStr} - ${endStr}`
    const date = startM.format(DATE_FORMAT_FULL)
    return (
      <Row icon={<AccessTimeIcon fontSize="small" />} onClick={() => handleCopy(`${date}, ${time}`, 'Date')}>
        <Typography className={classes.rowValuePrimary}>{time}</Typography>
        <Typography className={classes.rowValueSecondary}>{date}</Typography>
      </Row>
    )
  }

  const renderDealRow = () => {
    if (!event?.deal?.name) return null
    if (onPagePath(event.deal.id)) return null
    if (isProduction) return null // production keeps deal.name in the header

    const handleClick = () => {
      navigateTo(`/deals/edit/${event.deal?.id}`)
    }

    if (mobile) {
      return (
        <Row icon={<AttachMoneyIcon fontSize="small" />} onClick={handleClick}>
          <div className={classes.dealRowInline}>
            <Typography component="span" className={classes.crewLabel}>
              Deal:
            </Typography>
            <span className={classes.pill}>{event.deal.name}</span>
          </div>
        </Row>
      )
    }

    return (
      <Row icon={<AttachMoneyIcon fontSize="small" />} onClick={handleClick}>
        <Typography className={classes.rowValuePrimary}>Deal</Typography>
        <Typography className={classes.rowValueSecondary}>{event.deal.name}</Typography>
      </Row>
    )
  }

  const renderTotalRow = () => {
    if (!isProduction) return null
    const value = event.deal?.value
    if (typeof value !== 'number') return null
    const invoiceId = event.invoiceId
    const handleClick = invoiceId ? () => navigateTo(`/invoices/edit/${invoiceId}`) : undefined
    return (
      <Row icon={<MoneyCheckDollar fontSize="small" />} className={classes.rowSingleLine} onClick={handleClick}>
        <Typography className={classes.crewLabel}>
          Total: <span style={{ fontWeight: 600 }}>{formatCurrency({ options, value })}</span>
        </Typography>
      </Row>
    )
  }

  const renderCrewRow = () => {
    if (!isProduction) return null
    return (
      <CrewRow
        event={event}
        matchedCrew={matchedCrew}
        allCrews={allCrews}
        onCrewChange={readOnly ? undefined : onCrewChange}
      />
    )
  }

  const renderProgressRow = () => {
    if (!isProduction) return null
    const value = Math.max(0, Math.min(100, Number(event.progress ?? 0)))
    return (
      <ProgressBar
        value={value}
        color={matchedCrew?.color}
        onChange={!readOnly && onProgressChange ? (next) => onProgressChange(event, next) : undefined}
      />
    )
  }

  /**
   * Sales contact: grouped into one block on desktop (name / email / phone),
   * three separate icon+pill rows on mobile.
   * Production contact: name + phone only (one block on desktop, two icon+pill rows on mobile).
   */
  const renderContactSection = () => {
    if (isGoogleCalendar) return null
    return (
      <ContactSection
        event={event}
        contactName={contactName}
        mobile={mobile}
        isProduction={isProduction}
        navigateTo={navigateTo}
      />
    )
  }

  const renderDescriptionRow = () => {
    if (isProduction) return null
    if (!event?.note) return null
    return (
      <Row icon={<DescriptionIcon fontSize="small" />}>
        <Typography className={classes.rowValuePrimary}>Description</Typography>
        <HtmlContent content={event.note} className={classes.rowValueSecondary} />
      </Row>
    )
  }

  const renderCompanyRow = () => {
    if (mobile) return null // mobile uses contact pills only
    if (!event?.contact?.companyName) return null
    if (onPagePath(event.contact.companyId)) return null
    return (
      <Row
        icon={<PersonIcon fontSize="small" />}
        onClick={() => navigateTo(`/people/companies/${event.contact?.companyId}`)}
      >
        <Typography className={classes.rowValuePrimary}>Company</Typography>
        <Typography className={classes.rowValueSecondary}>{event.contact.companyName}</Typography>
      </Row>
    )
  }

  const renderAttendeesRow = () => {
    if (!event?.attendees || event.attendees <= 0) return null
    return (
      <Row icon={<PersonIcon fontSize="small" />}>
        <Typography className={classes.rowLabel}>Number of Guests</Typography>
        <Typography className={classes.rowValuePrimary}>{event.attendees}</Typography>
      </Row>
    )
  }

  const renderAddressRowMobile = () => {
    if (!mobile) return null
    if (isProduction) return null // production tucks address into the contact group
    if (!event?.address) return null
    return (
      <Row
        icon={<LocationOnIcon fontSize="small" />}
        onClick={() =>
          window.open(
            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address ?? '')}`,
            '_blank'
          )
        }
      >
        <span className={classes.pill}>{event.address}</span>
      </Row>
    )
  }

  const renderEstimatorRow = () => {
    if (isProduction) return null // production omits estimator (crew shown instead)
    if (!event?.estimator) return null
    return (
      <div className={classes.estimatorRow}>
        <EstimatorAvatar
          user={{
            id: estimatorUser?.user_id,
            firstName: estimatorUser?.user_metadata?.firstName,
            lastName: estimatorUser?.user_metadata?.lastName
          }}
          userPictures={
            estimatorUser?.user_id
              ? {
                  [estimatorUser.user_id.startsWith('auth0|')
                    ? estimatorUser.user_id
                    : `auth0|${estimatorUser.user_id}`]: estimatorUser.user_metadata?.customAvatar?.cdnValue ?? ''
                }
              : {}
          }
          borderColor={event.estimator.color}
          hideTooltip
        />
        <Typography className={classes.estimatorName}>{event.estimator.name}</Typography>
      </div>
    )
  }

  const body = (
    <>
      <div className={classes.header}>
        <div className={classes.headerText}>
          {isProduction && event?.deal?.id ? (
            <Typography
              variant="h3"
              className={classNames(classes.headerTitle, classes.link)}
              onClick={() => navigateTo(`/deals/edit/${event.deal?.id}`)}
            >
              {headerTitle}
            </Typography>
          ) : (
            <Typography variant="h3" className={classes.headerTitle}>
              {headerTitle}
            </Typography>
          )}
          {!!headerSubtitle && <Typography className={classes.headerSubtitle}>{headerSubtitle}</Typography>}
        </div>
        {mobile && (
          <IconButton onClick={handleClose} size="small" className={classes.closeButton} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      <div className={classes.scroll}>
        {isProduction ? (
          <>
            {renderContactSection()}
            {renderTimeRow()}
            {renderTotalRow()}
            {renderCrewRow()}
            {renderProgressRow()}
          </>
        ) : (
          <>
            {renderTimeRow()}
            {renderAddressRowMobile()}
            {renderDealRow()}
            {renderContactSection()}
            {renderDescriptionRow()}
            {renderCompanyRow()}
            {renderAttendeesRow()}
            {renderEstimatorRow()}
          </>
        )}
      </div>

      {!hideFooter && (
        <div className={classes.footer} style={{ justifyContent: isPastEvent ? 'flex-end' : 'space-between' }}>
          {!isPastEvent && (
            <Button variant="textNoPadding" className={classes.cancelButton} onClick={handleCancelOrDelete}>
              {cancelLabel}
            </Button>
          )}

          <Button onClick={handleEdit}>Edit</Button>
        </div>
      )}
    </>
  )

  if (mobile) {
    return (
      <Drawer
        anchor="bottom"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock
        className={classes.drawer}
        classes={{ paper: classes.drawerPaper }}
      >
        {body}
      </Drawer>
    )
  }

  return (
    <Popper
      key={event?.id}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      id={`event-preview-popper-${event?.id ?? 'none'}`}
      style={{ zIndex: 2000 }}
    >
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <div>
          <Paper className={classes.paper} style={{ minWidth: width, maxWidth: width }}>
            {body}
          </Paper>
        </div>
      </ClickAwayListener>
    </Popper>
  )
}
